/**
 * PRISM on a phone: a simplified layout for small touch screens. Find and
 * watch media, read and fix the basics of its metadata, upload from the phone
 * (including recording video or taking a photo with the camera, straight from
 * the browser, no app needed), and work through your tasks.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, CheckCircle2, ClipboardList, Film, Image as ImageIcon, Library, Loader2, Monitor, Search, Upload, UserRound, Video, X } from 'lucide-react';
import { formatDuration, type AssetSummary } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useAsset, useLibrary, useMe, useSaveMetadata, useTasks } from '../api/hooks';
import { progressLine, RUNNING_STATES, uploader, useUploads, type PlaceholderTarget, type UploadItem } from '../upload/uploader';
import { PlaceholderPicker } from '../components/PlaceholderPicker';
import { useLiveEvents } from '../api/events';
import { StatusBadge } from '../components/Status';
import { toast, Toasts } from '../components/Toasts';
import { aboutCount, bytes, relative } from '../lib/format';
import { useDesktopPreference } from './device';
import { LivePlayer } from '../player/LivePlayer';
import { Recorder, canRecordInPage } from './Recorder';
import { AccountDialog } from '../components/ChangePassword';
import { AssetGroupsBreadcrumb } from '../pages/groups/AssetGroups';

export function Mobile() {
  const me = useMe().data;
  // Live updates, as on the desktop: processing finishes, uploads appear.
  useLiveEvents(true);
  const uploads = useUploads();
  const running = uploads.filter((u) => RUNNING_STATES.includes(u.state));
  const active = running.length;
  // 1.6.4: away from the Upload page, a slim bar above the tabs shows the uploads' overall progress.
  const onUpload = useLocation().pathname.startsWith('/m/upload');
  const overall = active && !onUpload ? overallProgress(running) : null;
  return (
    <div className={`m-app${overall ? ' with-overall' : ''}`} data-testid="mobile">
      <main className="m-main">
        <Routes>
          <Route index element={<MLibrary />} />
          <Route path="assets/:id" element={<MAsset />} />
          <Route path="upload" element={<MUpload />} />
          <Route path="tasks" element={<MTasks />} />
          <Route path="*" element={<Navigate to="/m" replace />} />
        </Routes>
      </main>
      <nav className="m-tabs" aria-label="Sections">
        {overall && (
          <Link to="/m/upload" className="m-overall" data-testid="m-upload-overall" aria-label={`${overall.label}. Open Upload`}>
            <span className="m-overall-text">{overall.label}</span>
            <span className="m-progress"><span style={{ width: `${overall.pct}%` }} /></span>
          </Link>
        )}
        <NavLink end to="/m" className="m-tab"><Library size={20} /><span>Library</span></NavLink>
        {me?.permissions.includes('asset.upload') && (
          <NavLink to="/m/upload" className="m-tab"><Upload size={20} /><span>Upload{active ? ` (${active})` : ''}</span></NavLink>
        )}
        <NavLink to="/m/tasks" className="m-tab"><ClipboardList size={20} /><span>Tasks</span></NavLink>
      </nav>
      <Toasts />
    </div>
  );
}

/** The running uploads' bytes together: "Uploading 2 files · 42 %". */
export function overallProgress(running: Array<Pick<UploadItem, 'sent' | 'total' | 'state'>>): { pct: number; label: string } {
  const total = running.reduce((n, u) => n + u.total, 0);
  const sent = running.reduce((n, u) => n + Math.min(u.sent, u.total), 0);
  const pct = Math.floor((sent / Math.max(1, total)) * 100);
  const finishing = running.every((u) => u.state === 'finalizing');
  const label = finishing ? 'Finishing…' : `Uploading ${running.length === 1 ? '1 file' : `${running.length} files`} · ${pct} %`;
  return { pct: finishing ? 100 : pct, label };
}

/** An upload in plain words (1.6.4): Waiting, the progress, "Connection lost, resuming in 4 s", Finishing…, Uploaded… */
export function uploadStateLabel(u: Pick<UploadItem, 'state' | 'sent' | 'total' | 'rate' | 'error' | 'retryAt'>, now = Date.now()): string {
  switch (u.state) {
    case 'queued': return 'Waiting';
    case 'uploading': return u.sent > 0 ? progressLine(u.sent, u.total, u.rate) : 'Uploading · 0 %';
    case 'retrying': {
      const s = u.retryAt ? Math.max(0, Math.ceil((u.retryAt - now) / 1000)) : null;
      return s == null ? 'Connection lost, resuming soon' : s === 0 ? 'Connection lost, resuming now' : `Connection lost, resuming in ${s} s`;
    }
    case 'paused': return 'Paused';
    case 'finalizing': return 'Finishing…';
    case 'done': return 'Uploaded';
    case 'duplicate': return 'Already in the library';
    case 'cancelled': return 'Cancelled';
    default: return u.error ? `Failed: ${u.error}` : 'Failed';
  }
}

function MHeader({ title, back }: { title: string; back?: boolean }) {
  const navigate = useNavigate();
  const [, useDesktop] = useDesktopPreference();
  const me = useMe().data;
  const [account, setAccount] = useState(false);
  return (
    <header className="m-header">
      {back ? <button className="m-icon" onClick={() => navigate(-1)} aria-label="Back"><ArrowLeft size={20} /></button> : <img src="/prism.svg" alt="" className="m-logo" />}
      <h1>{title}</h1>
      <button className="m-icon" onClick={() => { useDesktop(true); navigate('/library'); }} aria-label="Desktop version" title="Desktop version" data-testid="m-desktop"><Monitor size={18} /></button>
      {me && <button className="m-icon" onClick={() => setAccount(true)} aria-label="My account" title="My account" data-testid="m-account"><UserRound size={18} /></button>}
      {me && account && <AccountDialog me={me} onClose={() => setAccount(false)} />}
    </header>
  );
}

function MLibrary() {
  const [q, setQ] = useState('');
  const lib = useLibrary({ q, status: [], kind: [], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false });
  const items = lib.data?.items ?? [];
  return (
    <>
      <MHeader title="Library" />
      <div className="m-search"><Search size={16} /><input type="search" placeholder="Search titles, keywords, notes…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the library" data-testid="m-search" /></div>
      {lib.isLoading ? <div className="m-empty"><Loader2 className="spin" /></div> : items.length === 0 ? <p className="m-empty">{q ? 'Nothing matches.' : 'The library is empty.'}</p> : (
        <ul className="m-list" data-testid="m-list">
          {items.map((a) => <MAssetRow key={a.id} a={a} />)}
        </ul>
      )}
      {items.length > 0 && <p className="m-note" data-testid="m-count">Showing {items.length} of {lib.data?.totalExact === false ? aboutCount(lib.data.total) : lib.data?.total ?? items.length}.</p>}
      {lib.hasNextPage && <button className="m-btn" style={{ margin: '0 16px 24px' }} onClick={() => void lib.fetchNextPage()} disabled={lib.isFetchingNextPage} data-testid="m-load-more">{lib.isFetchingNextPage ? 'Loading…' : 'Load more'}</button>}
    </>
  );
}

function MAssetRow({ a }: { a: AssetSummary }) {
  return (
    <li>
      <Link to={`/m/assets/${a.id}`} className="m-row" data-testid="m-asset">
        {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" loading="lazy" /> : <div className="m-thumb">{a.mediaKind === 'image' ? <ImageIcon size={18} /> : <Film size={18} />}</div>}
        <span className="m-row-main">
          <span className="m-row-title">{a.title}</span>
          <span className="m-row-sub">{a.durationTicks ? `${formatDuration(a.durationTicks)} · ` : ''}{relative(a.createdAt)}</span>
        </span>
        {a.status !== 'ready' && <StatusBadge status={a.status} />}
      </Link>
    </li>
  );
}

function MAsset() {
  const { id } = useParams<{ id: string }>();
  const q = useAsset(id);
  const me = useMe().data;
  const save = useSaveMetadata(id ?? '');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', description: '' });
  const a = q.data;
  if (q.isLoading) return <><MHeader title="" back /><div className="m-empty"><Loader2 className="spin" /></div></>;
  if (!a) return <><MHeader title="Not found" back /><p className="m-empty">This asset could not be loaded.</p></>;
  const canEdit = Boolean(me?.permissions.includes('asset.edit_metadata')) && !a.deleted;
  const description = String(a.metadata.f_description ?? '').replace(/<[^>]+>/g, '');
  const tags = (a.metadata.f_tags as string[] | undefined) ?? [];
  async function submit() {
    try {
      await save.mutateAsync({ revision: a!.metadataRevision, values: { f_title: form.title.trim() || a!.title, f_description: form.description } });
      setEditing(false);
      toast('Saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <>
      <MHeader title={a.title} back />
      {/* 1.4.0 (G2): the asset's groups, the breadcrumb only on a phone. */}
      <AssetGroupsBreadcrumb asset={a} />
      <div className="m-media">
        {a.live && !a.media.proxyUrl ? <LivePlayer live={a.live} />
          : a.mediaKind === 'image' && a.media.proxyUrl ? <img src={a.media.proxyUrl} alt={a.title} />
          : a.mediaKind === 'graphic' && a.media.posterUrl ? <img src={a.media.posterUrl} alt={a.title} />
          : a.media.proxyUrl ? <video src={a.media.proxyUrl} poster={a.media.posterUrl ?? undefined} controls playsInline preload="metadata" data-testid="m-video" />
          : a.status === 'placeholder' ? <MPlaceholderUpload asset={a} canUpload={Boolean(me?.permissions.includes('asset.upload')) && !a.deleted} />
          : <div className="m-empty"><StatusBadge status={a.status} /><p>{a.status === 'processing' ? 'Making a preview…' : 'No preview yet.'}</p></div>}
      </div>
      <section className="m-section">
        {editing ? (
          <div className="m-form">
            <label>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="m-title" /></label>
            <label>Description<textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="m-actions">
              <button className="m-btn ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button className="m-btn primary" disabled={save.isPending} onClick={() => void submit()} data-testid="m-save">Save</button>
            </div>
          </div>
        ) : (
          <>
            <h2>{a.title}</h2>
            <p className="m-row-sub">{a.durationTicks ? `${formatDuration(a.durationTicks)} · ` : ''}{a.originalFilename} · {relative(a.createdAt)}</p>
            {description && <p>{description}</p>}
            {tags.length > 0 && <div className="m-tags">{tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>}
            {canEdit && <button className="m-btn" onClick={() => { setForm({ title: a.title, description }); setEditing(true); }} data-testid="m-edit">Edit title and description</button>}
          </>
        )}
      </section>
      <p className="m-note"><Link to={`/assets/${a.id}`}>Open the full asset page</Link> for every field, storage and workflows.</p>
    </>
  );
}

/** A placeholder has no media yet: pick a file, take a photo or record a video straight into it. */
function MPlaceholderUpload({ asset, canUpload }: { asset: { id: string; assetCode: string; title: string }; canUpload: boolean }) {
  const [recording, setRecording] = useState(false);
  const inPage = canRecordInPage();
  const target: PlaceholderTarget = { id: asset.id, assetCode: asset.assetCode, title: asset.title };
  const busy = useUploads().some((u) => u.placeholder?.id === asset.id && !['done', 'cancelled', 'error', 'duplicate'].includes(u.state));
  const send = (file: File) => { uploader.add([file], { placeholder: target }); toast(`Uploading into ${asset.assetCode}`); };
  if (recording) return <Recorder onClose={() => setRecording(false)} onDone={(file) => { send(file); setRecording(false); }} />;
  return (
    <div className="m-empty" data-testid="m-placeholder-panel">
      <p>No media yet. {canUpload ? 'Upload a file into this asset.' : 'Your role cannot upload media into it.'}</p>
      {canUpload && (
        <>
          <label className="m-btn primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} {busy ? 'Uploading…' : 'Upload media'}
            <input type="file" hidden disabled={busy} accept="video/*,audio/*,image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f); e.target.value = ''; }} data-testid="m-placeholder-file" />
          </label>
          {inPage && !busy && <button className="m-btn" onClick={() => setRecording(true)}><Video size={16} style={{ verticalAlign: -3 }} /> Record video</button>}
          {!inPage && !busy && (
            <label className="m-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Video size={16} /> Record video
              <input type="file" hidden accept="video/*" capture="environment" onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f); e.target.value = ''; }} /></label>
          )}
          {!busy && (
            <label className="m-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Camera size={16} /> Take a photo
              <input type="file" hidden accept="image/*" capture="environment" onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f); e.target.value = ''; }} /></label>
          )}
        </>
      )}
    </div>
  );
}

function MUpload() {
  const uploads = useUploads();
  const inPage = canRecordInPage();
  const [recording, setRecording] = useState(false);
  const [target, setTarget] = useState<PlaceholderTarget | null>(null);
  if (recording) return <Recorder onClose={() => setRecording(false)} onDone={(file) => { uploader.add([file], target ? { placeholder: target } : {}); setRecording(false); setTarget(null); toast('Recording uploading'); }} />;
  const pick = (files: FileList | null) => {
    if (!files?.length) return;
    // A placeholder takes one file; the rest become new assets.
    uploader.add([...files], target ? { placeholder: target } : {});
    setTarget(null);
    toast(`${files.length} file${files.length === 1 ? '' : 's'} uploading`);
  };
  return (
    <>
      <MHeader title="Upload" />
      {/* 1.6.4: the uploads come first, so the progress is on screen right after choosing a file. */}
      {uploads.length > 0 && (
        <ul className="m-cards" data-testid="m-uploads">
          {[...uploads].reverse().map((u) => <MUploadCard key={u.localId} u={u} />)}
        </ul>
      )}
      <div className="m-note" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-testid="m-target">
        <span>Upload as</span><PlaceholderPicker value={target} onChange={setTarget} />
      </div>
      <div className="m-upload">
        {inPage ? (
          <button className="m-big" onClick={() => setRecording(true)} data-testid="m-open-recorder"><Video size={26} /><span>Record video<small>720p, 1080p or 4K</small></span></button>
        ) : (
          // Without PRISM's recorder (no HTTPS, or no getUserMedia/MediaRecorder), the phone's camera app records instead.
          <label className="m-big"><Video size={26} /><span>Record video</span>
            <input type="file" accept="video/*" capture="environment" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} data-testid="m-record" /></label>
        )}
        <label className="m-big"><Camera size={26} /><span>Take a photo</span>
          <input type="file" accept="image/*" capture="environment" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} /></label>
        <label className="m-big"><Upload size={26} /><span>Choose files</span>
          <input type="file" accept="video/*,audio/*,image/*" multiple onChange={(e) => { pick(e.target.files); e.target.value = ''; }} data-testid="m-files" /></label>
      </div>
      <p className="m-note" data-testid="m-iphone-note">
        On an iPhone, large videos may take a moment to prepare before the upload starts. In the picker, Options → Current skips the conversion and keeps the full quality.
        {inPage && ' For camera features PRISM’s recorder lacks, such as slow motion or Cinematic mode, record in the Camera app and upload with Choose files.'}
      </p>
      <p className="m-note">Uploads continue while you browse PRISM, and resume after a dropped connection. Keep PRISM open until they finish; the screen stays on meanwhile.</p>
    </>
  );
}

/** One upload: its name, a bar, and the progress or state in plain words; Cancel, Open or Upload anyway. */
function MUploadCard({ u }: { u: UploadItem }) {
  const [now, setNow] = useState(() => Date.now());
  // A countdown while waiting to resume.
  useEffect(() => {
    if (u.state !== 'retrying') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [u.state]);
  const pct = u.state === 'done' || u.state === 'finalizing' ? 100 : (u.sent / Math.max(1, u.total)) * 100;
  const bar = ['uploading', 'retrying', 'finalizing', 'paused'].includes(u.state);
  return (
    <li className={`m-card ${u.state}`} data-testid="m-upload-card" data-state={u.state}>
      <span className="m-row-main">
        <span className="m-row-title">{u.file.name}</span>
        <span className="m-row-sub" data-testid="m-upload-status">{bytes(u.total)} · {uploadStateLabel(u, now)}</span>
        {bar && <span className={`m-progress${u.state === 'finalizing' ? ' pulse' : ''}`}><span style={{ width: `${pct}%` }} /></span>}
      </span>
      {u.state === 'duplicate' && u.duplicate ? (
        <span style={{ display: 'flex', gap: 6, flex: 'none' }}>
          <Link className="m-btn small" to={`/m/assets/${u.duplicate.existingAssetId}`}>Open</Link>
          <button className="m-btn small" onClick={() => uploader.importAnyway(u.localId)} data-testid="m-import-anyway">Upload anyway</button>
        </span>
      ) : u.state === 'done' && u.assetId ? <Link to={`/m/assets/${u.assetId}`} aria-label="Open" data-testid="m-upload-open"><CheckCircle2 size={22} color="var(--ready)" /></Link>
        : ['queued', 'uploading', 'retrying'].includes(u.state) ? <button className="m-icon" aria-label="Cancel" onClick={() => void uploader.cancel(u.localId)} data-testid="m-upload-cancel"><X size={18} /></button> : null}
    </li>
  );
}

function MTasks() {
  const tasks = useTasks({ scope: 'mine', status: 'active' });
  const items = tasks.data?.items ?? [];
  async function done(id: string) {
    try {
      await api(`/api/v1/tasks/${id}`, { method: 'PATCH', json: { status: 'done' } });
      await tasks.refetch();
      toast('Marked done');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <>
      <MHeader title="Tasks" />
      {items.length === 0 ? <p className="m-empty">Nothing waiting for you.</p> : (
        <ul className="m-list" data-testid="m-tasks">
          {items.map((t) => (
            <li key={t.id} className="m-row static">
              <span className="m-row-main">
                <span className="m-row-title">{t.title}</span>
                <span className="m-row-sub" style={t.overdue ? { color: 'var(--danger)' } : undefined}>
                  {t.subject ? (t.subject.type === 'asset' ? <Link to={`/m/assets/${t.subject.id}`}>{t.subject.name}</Link> : t.subject.name) : 'No subject'}
                  {t.dueAt ? ` · due ${relative(t.dueAt)}` : ''}{t.createdBy ? ` · from ${t.createdBy.name}` : ''}
                </span>
                {t.details && <span className="m-row-sub">{t.details}</span>}
              </span>
              {t.canChangeStatus && <button className="m-btn small" onClick={() => void done(t.id)}>Done</button>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
